import React, { useEffect, useState, useCallback, useRef } from 'react'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import dayjs from 'dayjs'
import useToastContext from 'hooks/useToastContext'
import { exportReportToExcel } from 'utils/reportExport'

// ═══ CHART.JS IMPORTS ═══
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
)

// ═══ PAGINATION CONSTANTS ═══
const ITEMS_PER_PAGE = 10;

const LaborCostReport = ({ selectedEmployees, dateRange, onExportReady }) => {
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const notification = useToastContext()

  // ═══ PAGINATION STATE ═══
  const [currentPage, setCurrentPage] = useState(1)
  
  // ═══ REFS ═══
  const isRegistered = useRef(false);
  const isExportingRef = useRef(false);

  useEffect(() => {
    const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
    const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
    fetchData(start, end, currentPage)
  }, [selectedEmployees, dateRange, currentPage])

  // ═══ RESET PAGE TO 1 WHENEVER FILTER CHANGES ═══
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedEmployees, dateRange])

  const fetchData = async (start, end, page) => {
    setIsLoading(true)
    try {
      const employeeIDs = selectedEmployees.map(e => e.value)
      const payload = {
        startDate: start,
        endDate: end,
        page: page,
        limit: ITEMS_PER_PAGE,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      const result = await apiGet(apiPath.reportLaborCost, payload)
      
      if (result?.data?.success) {
        let finalData = []
        const rawResults = result.data.results
        
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          finalData = rawResults.results
          setTotalRecords(rawResults.total || 0)
        } else if (Array.isArray(rawResults)) {
          finalData = rawResults
          setTotalRecords(rawResults.length)
        }
        setData(finalData || [])
      } else {
        setData([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching labor cost report:', error)
      notification.error('Error fetching report data')
      setData([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }

  // ═══ HANDLE EXPORT ═══
  const handleExport = useCallback(async () => {
    if (isExportingRef.current) return;
    
    isExportingRef.current = true;
    setIsExporting(true);
    
    try {
      const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
      const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
      const employeeIDs = selectedEmployees.map(e => e.value)
      
      const payload = {
        startDate: start,
        endDate: end,
        export: true,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      
      const result = await apiGet(apiPath.reportLaborCost, payload)
      
      if (result?.data?.success) {
        let allData = []
        const rawResults = result.data.results
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          allData = rawResults.results
        } else if (Array.isArray(rawResults)) {
          allData = rawResults
        }
        
        if (allData.length > 0) {
          exportReportToExcel(allData, 'Labor Cost', 'labor_cost')
        } else {
          notification.error('No data available to export')
        }
      } else {
        notification.error('Failed to fetch all data for export')
      }
    } catch (error) {
      console.error('Export failed:', error)
      notification.error('Export failed')
    } finally {
      setIsExporting(false)
      isExportingRef.current = false;
    }
  }, [dateRange, selectedEmployees, notification])

  // ═══ REGISTER EXPORT FUNCTION WITH PARENT ═══
  useEffect(() => {
    if (onExportReady && !isRegistered.current && data.length > 0) {
      onExportReady(handleExport);
      isRegistered.current = true;
    }
    
    return () => {
      if (onExportReady && isRegistered.current) {
        onExportReady(null);
        isRegistered.current = false;
      }
    }
  }, [handleExport, onExportReady, data]);

  // ═══ PAGINATION CALCULATIONS ═══
  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE)

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  if (isLoading) return null;
  if (!data.length && !isLoading) return <EmptyState />

  // Calculate Totals for Summary Cards
  const totalGrossPay = data.reduce((sum, row) => sum + (row.grossPay || 0), 0)
  const totalNetPay = data.reduce((sum, row) => sum + (row.netPay || 0), 0)
  const totalTax = data.reduce((sum, row) => sum + (row.taxWithheld || 0), 0)
  const totalOvertimePay = data.reduce((sum, row) => sum + (row.overtimePay || 0), 0)

  // ═══ CHART 1: GROSS vs NET PAY (STACKED BAR) ═══
  // Visually shows how much of Gross pay is lost to Tax
  const barChartOptions = {
    indexAxis: 'y', // Horizontal Bar
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => `$${context.parsed.x.toFixed(2)}`
        }
      }
    },
    scales: {
      x: { 
        stacked: true, 
        grid: { display: false }, 
        ticks: { callback: (val) => `$${val}`, font: { size: 10 } } 
      },
      y: { 
        stacked: true, 
        grid: { display: false }, 
        ticks: { font: { size: 10 } } 
      }
    }
  };

  const barChartData = {
    labels: data.map(item => item.employeeName || 'Unknown'),
    datasets: [
      {
        label: 'Net Pay',
        data: data.map(item => item.netPay || 0),
        backgroundColor: '#34D399', // Soft Green (Matching UI)
        borderColor: '#10B981',
        borderWidth: 1,
        borderRadius: 3,
      },
      {
        label: 'Tax Withheld',
        data: data.map(item => item.taxWithheld || 0),
        backgroundColor: '#F87171', // Soft Red (Matching UI)
        borderColor: '#EF4444',
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };

  // ═══ CHART 2: GROSS PAY DISTRIBUTION (DOUGHNUT) ═══
  // Highlights who is consuming the largest share of the payroll budget
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: $${context.parsed.toFixed(2)} (${percentage}%)`;
          }
        }
      }
    }
  };

  const doughnutChartData = {
    labels: data.map(item => item.employeeName || 'Unknown'),
    datasets: [
      {
        label: 'Gross Pay',
        data: data.map(item => item.grossPay || 0),
        backgroundColor: [
          '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4'
        ], // Vibrant but professional distinct colors
        borderWidth: 3,
        borderColor: '#ffffff'
      },
    ],
  };

  return (
    <div>
      {/* Summary Cards - aligned to Homebase style */}
      <div className='grid grid-cols-3 gap-4 mb-4'>
        <SummaryCard label='Total Gross Pay' value={`$${totalGrossPay.toFixed(2)}`} color='blue' />
        <SummaryCard label='Total Net Pay' value={`$${totalNetPay.toFixed(2)}`} color='green' />
        <SummaryCard label='Total Tax Withheld' value={`$${totalTax.toFixed(2)}`} color='red' />
      </div>
      
      {/* Optional 2nd row for Labor Specifics */}
      <div className='grid grid-cols-3 gap-4 mb-6'>
        <SummaryCard label='Total Overtime Cost' value={`$${totalOvertimePay.toFixed(2)}`} color='orange' />
        <div className='rounded-lg border p-4 bg-white border-gray-200'>
          <p className='text-xs text-gray-500 mb-1'>Total Employees</p>
          <p className='text-2xl font-bold text-gray-800'>{data.length}</p>
        </div>
        <div className='rounded-lg border p-4 bg-white border-gray-200'>
          <p className='text-xs text-gray-500 mb-1'>Avg. Gross Pay</p>
          <p className='text-2xl font-bold text-gray-800'>
            ${data.length > 0 ? (totalGrossPay / data.length).toFixed(2) : '0.00'}
          </p>
        </div>
      </div>

      {/* ═══ CHARTS SECTION ═══ */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
        
        {/* Chart 1: Gross vs Net Pay Stacked Bar */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Gross vs. Net Pay (Tax Visibility)</h3>
          <div className='h-64 w-full'>
            <Bar options={barChartOptions} data={barChartData} />
          </div>
        </div>

        {/* Chart 2: Gross Pay Distribution Doughnut */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Gross Pay Distribution</h3>
          <div className='h-64 w-full'>
            <Doughnut options={doughnutOptions} data={doughnutChartData} />
          </div>
        </div>
      </div>

      {/* ═══ TABLE ═══ */}
      <div className='overflow-x-auto rounded-lg border border-[#E1E6EE]'>
        <table className='w-full text-xs text-left'>
          <thead className='text-xs text-gray-600 bg-[#EBEFF8]'>
            <tr>
              <th className='py-3 px-4 font-medium'>Sr no.</th>
              <th className='py-3 px-4 font-medium'>Employee</th>
              <th className='py-3 px-4 font-medium'>ID</th>
              <th className='py-3 px-4 font-medium'>Hourly Rate</th>
              <th className='py-3 px-4 font-medium'>Reg. Hrs</th>
              <th className='py-3 px-4 font-medium'>OT Hrs</th>
              <th className='py-3 px-4 font-medium'>Gross Pay</th>
              <th className='py-3 px-4 font-medium'>Tax</th>
              <th className='py-3 px-4 font-medium'>Net Pay</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              return (
                <tr key={i} className='bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                  <td className='py-3 px-4 font-medium text-gray-800'>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                  <td className='py-3 px-4 font-medium text-gray-800'>{row.employeeName}</td>
                  <td className='py-3 px-4 text-gray-600'>{row.employeeId}</td>
                  <td className='py-3 px-4 text-gray-600'>${row.hourlyRate.toFixed(2)}</td>
                  <td className='py-3 px-4 text-gray-600'>{row.regularHours.toFixed(1)}h</td>
                  <td className='py-3 px-4 text-gray-600'>{row.overtimeHours.toFixed(1)}h</td>
                  <td className='py-3 px-4 font-semibold text-gray-900'>${row.grossPay.toFixed(2)}</td>
                  <td className='py-3 px-4 text-red-500'>${row.taxWithheld.toFixed(2)}</td>
                  <td className='py-3 px-4 font-bold text-green-600'>${row.netPay.toFixed(2)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ═══ PAGINATION FOOTER ═══ */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-4 pt-2 me-8 border-t border-gray-100'>
          <div className='text-sm text-gray-500'>
            Total <span className='font-medium text-gray-700'>{totalRecords}</span> Records
          </div>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => currentPage === 1 ? null : handlePageChange(currentPage - 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors border ${
                currentPage === 1 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Prev
            </button>
            
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-8 h-8 rounded text-sm font-medium flex items-center border justify-center transition-colors ${
                    currentPage === pageNum
                      ? 'bg-[#1E40AF] text-white'
                      : 'text-gray-600 bg-transparent hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
            
            <button
              onClick={() => currentPage === totalPages ? null : handlePageChange(currentPage + 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                currentPage === totalPages 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default LaborCostReport

// ─── Shared UI helpers ─────────────────────────────────────────────────────────
export const SummaryCard = ({ label, value, color }) => {
  const colorMap = {
    blue: 'text-[#2563EB] bg-blue-50 border-blue-100',
    green: 'text-green-600 bg-green-50 border-green-100',
    orange: 'text-orange-500 bg-orange-50 border-orange-100',
    red: 'text-red-500 bg-red-50 border-red-100',
    purple: 'text-purple-600 bg-purple-50 border-purple-100'
  }
  return (
    <div className={`rounded-lg border p-4 ${colorMap[color] || colorMap.blue}`}>
      <p className='text-xs text-gray-500 mb-1'>{label}</p>
      <p className={`text-2xl font-bold ${colorMap[color]?.split(' ')[0]}`}>{value}</p>
    </div>
  )
}

export const LoadingState = () => (
  <div className='flex items-center justify-center py-16 text-gray-400'>
    <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]' />
    <span className='ml-3 text-sm'>Loading report...</span>
  </div>
)

export const EmptyState = () => (
  <div className='flex flex-col items-center justify-center py-16 text-gray-400'>
    <p className='text-sm font-medium'>No payroll data found for the selected filters</p>
    <p className='text-xs mt-1'>Try adjusting the date range or employee selection</p>
  </div>
)